import { Link } from "react-router-dom";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Stethoscope, Ambulance, HeartPulse, FlaskConical, Pill, ScanLine, ShieldCheck,
  ArrowRight, Video, MapPin, PhoneCall, Sparkles, Hospital, Search, Play,
} from "lucide-react";

const ECO_IMG_1 = "https://images.unsplash.com/photo-1637059824899-a441006a6875?fm=jpg&q=80&w=1200&auto=format&fit=crop&ixlib=rb-4.1.0";
const AMB_IMG = "https://images.unsplash.com/photo-1619025873875-59dfdd2bbbd6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxhbWJ1bGFuY2UlMjBlbWVyZ2VuY3klMjBob3NwaXRhbCUyMG1lZGljYWwlMjBjYXJlfGVufDB8fHx8MTc4OTMxMzY4N3ww&ixlib=rb-4.1.0&q=85";
const LAB_IMG = "https://images.unsplash.com/photo-1639772823849-6efbd173043c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxwaGFybWFjeSUyMGxhYm9yYXRvcnklMjBtZWRpY2luZSUyMHRlc3Rpbmd8ZW58MHx8fHwxNzg5MzEzNjk0fDA&ixlib=rb-4.1.0&q=85";
const PHARM_IMG = "https://images.unsplash.com/photo-1580281657527-47f249e8f4df?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHw0fHxwaGFybWFjeSUyMGxhYm9yYXRvcnklMjBtZWRpY2luZSUyMHRlc3Rpbmd8ZW58MHx8fHwxNzg5MzEzNjk0fDA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  return (
    <div className="text-white">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-40" />
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-teal-500/10 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-[400px] h-[400px] rounded-full bg-red-500/10 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-24 lg:pt-24 lg:pb-32">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 fade-up">
              <Badge className="bg-teal-500/15 text-teal-300 border-teal-500/30 mb-6" data-testid="hero-badge">
                <Sparkles className="w-3 h-3 mr-1" /> AI-powered healthcare • Patna & beyond
              </Badge>
              <div className="mb-5 flex items-center gap-4">
                <img src="/logo-mark.webp" alt="MedHOne M" className="w-14 h-14 object-contain drop-shadow-[0_8px_24px_rgba(20,184,166,0.35)]" />
                <div>
                  <div className="font-display text-2xl font-extrabold tracking-tight">MedH<span className="text-teal-400">One</span></div>
                  <div className="text-[10px] uppercase tracking-[0.28em] text-teal-300/80">One Platform, Every Care</div>
                </div>
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
                Two ecosystems.<br />
                <span className="gradient-text">One AI care platform.</span>
              </h1>
              <p className="mt-6 text-base sm:text-lg text-slate-300 max-w-2xl leading-relaxed">
                From OPD consultation and AI prescription reading to home lab collection, pharmacy delivery,
                emergency bed reservation and ambulance dispatch — MedHOne AI Care connects your entire
                treatment journey in real time.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/ai-triage" data-testid="cta-ai-triage">
                  <Button size="lg" className="bg-white hover:bg-slate-100 text-slate-950 font-semibold h-12 px-6">
                    <Sparkles className="w-4 h-4 mr-2" /> Ask MedHOne AI Care
                  </Button>
                </Link>
                <Link to="/book-opd" data-testid="cta-book-opd">
                  <Button size="lg" className="bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold h-12 px-6">
                    Book OPD Consultation <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/emergency" data-testid="cta-emergency">
                  <Button size="lg" variant="outline" className="border-red-500/50 text-red-300 hover:bg-red-500/10 h-12 px-6">
                    <Ambulance className="w-4 h-4 mr-2" /> Emergency Care
                  </Button>
                </Link>
              </div>

              <div className="mt-10 grid grid-cols-3 max-w-lg gap-4">
                <Stat n="24×7" l="Emergency Desk" />
                <Stat n="< 12min" l="Ambulance ETA" />
                <Stat n="4 tier" l="Ambulance Types" />
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-teal-500/10">
                <img src={ECO_IMG_1} alt="Doctor consultation" className="w-full h-[420px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/20 to-transparent" />
                <div className="absolute bottom-0 p-6 w-full">
                  <div className="flex items-center gap-2 text-xs text-teal-300 uppercase tracking-widest mb-2">
                    <Video className="w-3 h-3" /> Video OPD live
                  </div>
                  <div className="font-display text-2xl font-bold">Consult specialists from home</div>
                </div>
              </div>
              <div className="absolute -bottom-6 -left-6 bg-slate-900 border border-slate-800 rounded-2xl p-4 shadow-xl hidden sm:block">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-red-600/20 flex items-center justify-center">
                    <HeartPulse className="w-5 h-5 text-red-400" />
                  </div>
                  <div>
                    <div className="text-xs text-slate-400">Live beds available</div>
                    <div className="font-mono text-lg font-bold">137</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DEMO VIDEO */}
      <section className="bg-slate-950 py-16 sm:py-20 border-t border-white/5" data-testid="demo-video-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <Badge className="bg-teal-500/15 text-teal-300 border-teal-500/30 mb-4">
              <Play className="w-3 h-3 mr-1" /> 1 मिनट का डेमो • Hindi narration
            </Badge>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
              देखिए — दोनों care ecosystems कैसे काम करते हैं
            </h2>
            <p className="mt-3 text-slate-400 max-w-2xl mx-auto text-sm sm:text-base">
              Watch how a patient goes from AI triage → video OPD → home lab tests → medicine delivery,
              and how one tap reserves an ICU bed with live ambulance tracking.
            </p>
          </div>
          <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-teal-500/10">
            <video
              data-testid="demo-video"
              className="w-full aspect-video bg-slate-900"
              src="/medhone-demo.mp4"
              poster="/demo-poster.jpg"
              controls
              playsInline
              preload="metadata"
            />
          </div>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
            <DemoStat icon={Sparkles} label="AI triage" sub="आध्या से पूछें" />
            <DemoStat icon={Video} label="Video OPD" sub="घर बैठे इलाज" />
            <DemoStat icon={FlaskConical} label="Home lab tests" sub="घर पर सैंपल" />
            <DemoStat icon={Ambulance} label="Live ambulance" sub="लाइव ट्रैकिंग" />
          </div>
        </div>
      </section>

      {/* TWO ECOSYSTEMS */}
      <section className="bg-slate-50 text-slate-900 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <div className="text-xs font-semibold uppercase tracking-widest text-teal-700 mb-3">Complete network</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
              A dedicated ecosystem for every moment
            </h2>
            <p className="mt-3 text-slate-600 max-w-2xl mx-auto">
              Primary care for planned journeys. Emergency care for critical moments. Both connected by one intelligent workflow.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <EcosystemCard
              badge="Primary Care"
              badgeClass="bg-teal-100 text-teal-800"
              title="OPD → Rx → Tests → Pharmacy → Follow-up"
              desc="Book a video or clinic OPD. Our AI prescription reader auto-routes tests and medicines to the right partner — with home collection and delivery."
              image={ECO_IMG_1}
              steps={[
                { i: Stethoscope, t: "Book OPD (video / clinic)" },
                { i: ScanLine, t: "AI reads prescription" },
                { i: FlaskConical, t: "Lab test with home collection" },
                { i: Pill, t: "Medicine home delivery" },
                { i: HeartPulse, t: "Automatic follow-up alert" },
              ]}
              ctaTo="/book-opd"
              ctaLabel="Start Primary Care"
              testid="eco-primary-card"
            />
            <EcosystemCard
              badge="Emergency Care"
              badgeClass="bg-red-100 text-red-800"
              title="Bed reservation → Ambulance → Hospital transfer"
              desc="Reserve an ICU / NICU / general bed before the ambulance leaves. Choose Basic, ALS or ICU ambulance — track dispatch to arrival."
              image={AMB_IMG}
              steps={[
                { i: Hospital, t: "Select hospital & bed" },
                { i: Ambulance, t: "Pick ambulance tier" },
                { i: MapPin, t: "Live pickup & transfer" },
                { i: ShieldCheck, t: "Priority handover at ER" },
              ]}
              ctaTo="/emergency"
              ctaLabel="Emergency Access"
              ctaVariant="destructive"
              testid="eco-emergency-card"
            />
          </div>
        </div>
      </section>

      {/* QUICK FACILITY GRID */}
      <section className="bg-white py-20 text-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-teal-700 mb-2">Facility search</div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
                Or find any facility directly
              </h2>
            </div>
            <Link to="/search"><Button variant="outline" data-testid="all-facilities-btn">All facilities <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <FacilityTile icon={Stethoscope} label="Doctors" to="/book-opd" testid="tile-doctors" color="bg-teal-500" />
            <FacilityTile icon={Hospital} label="Hospitals" to="/emergency" testid="tile-hospitals" color="bg-cyan-500" />
            <FacilityTile icon={FlaskConical} label="Diagnostic Labs" to="/lab-tests" testid="tile-labs" color="bg-emerald-500" />
            <FacilityTile icon={Pill} label="Pharmacies" to="/pharmacy" testid="tile-pharmacies" color="bg-amber-500" />
          </div>
        </div>
      </section>

      {/* FOOTER STRIP */}
      <section className="bg-slate-950 py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid md:grid-cols-3 gap-6 items-center">
          <div className="flex items-center gap-4">
            <img src="/logo-mark.webp" alt="MedHOne M" className="w-12 h-12 object-contain drop-shadow-[0_4px_16px_rgba(20,184,166,0.35)]" />
            <div>
              <div className="font-display text-2xl font-bold">Have a medical emergency?</div>
              <div className="text-slate-400 text-sm mt-1">One tap dispatch. Nearest ambulance. Verified ER bed.</div>
            </div>
          </div>
          <div className="md:col-span-2 flex flex-wrap gap-3 md:justify-end">
            <a href="tel:108" data-testid="footer-108" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-red-600 hover:bg-red-500 text-white font-semibold emergency-pulse">
              <PhoneCall className="w-4 h-4" /> Call 108
            </a>
            <Link to="/emergency"><Button size="lg" className="bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold" data-testid="footer-book-ambulance">Book ambulance</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}

const Stat = ({ n, l }) => (
  <div>
    <div className="font-display text-2xl font-bold text-teal-300">{n}</div>
    <div className="text-xs text-slate-400 uppercase tracking-wider">{l}</div>
  </div>
);

const DemoStat = ({ icon: Icon, label, sub }) => (
  <div className="rounded-2xl border border-white/10 bg-slate-900/60 px-3 py-4">
    <Icon className="w-5 h-5 text-teal-400 mx-auto mb-2" />
    <div className="text-sm font-semibold text-white">{label}</div>
    <div className="text-xs text-slate-400 mt-0.5">{sub}</div>
  </div>
);

function EcosystemCard({ badge, badgeClass, title, desc, image, steps, ctaTo, ctaLabel, ctaVariant, testid }) {
  return (
    <Card data-testid={testid} className="overflow-hidden card-lift border-slate-200 rounded-3xl">
      <div className="relative h-48">
        <img src={image} alt={badge} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-white/95 via-white/40 to-transparent" />
        <Badge className={`${badgeClass} absolute top-4 left-4 border-0 font-semibold`}>{badge}</Badge>
      </div>
      <div className="p-6">
        <h3 className="font-display text-xl sm:text-2xl font-bold">{title}</h3>
        <p className="mt-2 text-slate-600 text-sm">{desc}</p>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {steps.map((s, i) => (
            <div key={i} className="flex items-center gap-2 text-sm text-slate-700">
              <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center">
                <s.i className="w-4 h-4 text-teal-700" />
              </div>
              {s.t}
            </div>
          ))}
        </div>
        <div className="mt-6">
          <Link to={ctaTo}>
            <Button
              className={ctaVariant === "destructive"
                ? "bg-red-600 hover:bg-red-500 text-white"
                : "bg-slate-900 hover:bg-slate-800 text-white"}
              data-testid={`${testid}-cta`}
            >
              {ctaLabel} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </Card>
  );
}

function FacilityTile({ icon: Icon, label, to, testid, color }) {
  return (
    <Link to={to} data-testid={testid}>
      <Card className="p-6 card-lift border-slate-200 rounded-2xl cursor-pointer">
        <div className={`w-11 h-11 rounded-xl ${color} flex items-center justify-center mb-4 text-white`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="font-display text-lg font-bold">{label}</div>
        <div className="text-xs text-slate-500 mt-1">Browse & book instantly</div>
      </Card>
    </Link>
  );
}
